import { AlertCircle, Ban, LogIn } from "lucide-react";
import { Button } from "../ui/button";
import { Card } from "../ui/card";

interface ErrorStateHandlerProps {
  error: Error | null;
  onRetry?: () => void;
  onLogin?: () => void;
  className?: string;
}

export function ErrorStateHandler({
  error,
  onRetry,
  onLogin,
  className = "",
}: ErrorStateHandlerProps) {
  if (!error) return null;

  const supportDiscordUrl =
    import.meta.env.VITE_SUPPORT_DISCORD_URL || "https://discord.gg/ai-edge";
  const visitedObjects = new WeakSet<object>();

  const containsSuspensionKeyword = (value?: string | null) =>
    typeof value === "string" && value.toLowerCase().includes("suspend");

  const extractSuspensionMessage = (value?: unknown): string | undefined => {
    if (!value) return undefined;

    if (typeof value === "string") {
      if (containsSuspensionKeyword(value)) {
        return value;
      }

      const trimmed = value.trim();
      if (trimmed.startsWith("{") || trimmed.startsWith("[")) {
        try {
          const parsed = JSON.parse(trimmed);
          return extractSuspensionMessage(parsed);
        } catch {
          return undefined;
        }
      }
      return undefined;
    }

    if (typeof value === "object") {
      if (visitedObjects.has(value as object)) {
        return undefined;
      }

      visitedObjects.add(value as object);
      const record = value as Record<string, unknown>;
      const detailCandidate =
        record?.detail ?? record?.message ?? record?.error ?? record?.status;

      const nestedMessage = extractSuspensionMessage(detailCandidate);
      if (nestedMessage) {
        return nestedMessage;
      }

      for (const nestedValue of Object.values(record)) {
        const result = extractSuspensionMessage(nestedValue);
        if (result) {
          return result;
        }
      }
    }

    return undefined;
  };

  const suspensionMessage =
    extractSuspensionMessage(error) ||
    extractSuspensionMessage((error as any)?.message) ||
    extractSuspensionMessage((error as any)?.response?.data);

  if (suspensionMessage) {
    const normalizedMessage = containsSuspensionKeyword(suspensionMessage)
      ? suspensionMessage
      : "This account has been suspended.";

    return (
      <div
        className={`flex flex-col items-center justify-center min-h-[400px] ${className}`}
      >
        <Card className="p-8 text-center max-w-md">
          <div className="flex items-center justify-center w-16 h-16 rounded-full bg-amber-500/10 mb-6 mx-auto">
            <Ban className="h-8 w-8 text-amber-400" />
          </div>

          <h3 className="text-xl font-semibold text-foreground mb-2">
            Account Suspended
          </h3>

          <p className="text-muted-foreground mb-4 leading-relaxed">
            {containsSuspensionKeyword(normalizedMessage)
              ? normalizedMessage
              : `${normalizedMessage} Please contact support if you believe this is a mistake.`}
          </p>

          <p className="text-sm text-muted-foreground mb-6">
            Need help reactivating access? Reach out to our team on our AI Edge
            server on Discord.
          </p>

          <Button
            className="min-w-[160px]"
            onClick={() => {
              window.open(supportDiscordUrl, "_blank", "noopener");
            }}
          >
            Open Discord
          </Button>
        </Card>
      </div>
    );
  }

  // Check if it's an authentication error
  const isAuthError =
    error.message?.includes("Authentication required") ||
    error.message?.includes("Unauthorized") ||
    error.message?.includes("401");

  // Check if it's a network error
  const isNetworkError =
    error.message?.includes("Network") ||
    error.message?.includes("Failed to fetch") ||
    error.message?.includes("ERR_NETWORK");

  if (isAuthError) {
    return (
      <div
        className={`flex flex-col items-center justify-center min-h-[400px] ${className}`}
      >
        <Card className="p-8 text-center max-w-md">
          <div className="flex items-center justify-center w-16 h-16 rounded-full bg-blue-500/10 mb-6 mx-auto">
            <LogIn className="h-8 w-8 text-blue-400" />
          </div>

          <h3 className="text-xl font-semibold text-foreground mb-2">
            Authentication Required
          </h3>

          <p className="text-muted-foreground mb-6 leading-relaxed">
            Please sign in to view your optimization studies and trading
            strategies.
          </p>

          {onLogin && (
            <Button onClick={onLogin} className="min-w-[140px]">
              <LogIn className="h-4 w-4 mr-2" />
              Sign In with Google
            </Button>
          )}
        </Card>
      </div>
    );
  }

  if (isNetworkError) {
    return (
      <div
        className={`flex flex-col items-center justify-center min-h-[400px] ${className}`}
      >
        <Card className="p-8 text-center max-w-md">
          <div className="flex items-center justify-center w-16 h-16 rounded-full bg-red-500/10 mb-6 mx-auto">
            <AlertCircle className="h-8 w-8 text-red-400" />
          </div>

          <h3 className="text-xl font-semibold text-foreground mb-2">
            Connection Error
          </h3>

          <p className="text-muted-foreground mb-6 leading-relaxed">
            Unable to connect to the server. Please check your internet
            connection and try again.
          </p>

          {onRetry && (
            <Button
              onClick={onRetry}
              variant="outline"
              className="min-w-[140px]"
            >
              
              Try Again
            </Button>
          )}
        </Card>
      </div>
    );
  }

  // Generic error state
  return (
    <div
      className={`flex flex-col items-center justify-center min-h-[400px] ${className}`}
    >
      <Card className="p-8 text-center max-w-md">
        <div className="flex items-center justify-center w-16 h-16 rounded-full bg-red-500/10 mb-6 mx-auto">
          <AlertCircle className="h-8 w-8 text-red-400" />
        </div>

        <h3 className="text-xl font-semibold text-foreground mb-2">
          Something went wrong
        </h3>

        <p className="text-muted-foreground mb-4 leading-relaxed">
          {error.message ||
            "An unexpected error occurred while loading your data."}
        </p>

        <details className="text-left mb-6">
          <summary className="cursor-pointer text-sm text-muted-foreground hover:text-foreground transition-colors">
            Technical Details
          </summary>
          <pre className="mt-2 p-3 bg-muted/50 rounded text-xs overflow-auto max-h-32">
            {error.stack || error.message}
          </pre>
        </details>

        {onRetry && (
          <Button onClick={onRetry} variant="outline" className="min-w-[140px]">
            
            Try Again
          </Button>
        )}
      </Card>
    </div>
  );
}
